import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Grid, MenuItem, Stack, IconButton, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import userService from 'api/userService';

export default function AdminCreateRider() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        gender: 'male',
        vehicle: 'bicycle',
        password: 'Password123!' // Default temporary password
    });

    const createMutation = useMutation({
        mutationFn: (data) => userService.adminCreateRider(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['adminAllRiders']);
            navigate('/admin/riders');
        },
        onError: (err) => {
            setError(err?.response?.data?.message || 'Failed to create rider profile.');
        }
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.email || !formData.phoneNumber) {
            setError('Email and Phone Number are required.');
            return;
        }
        createMutation.mutate(formData);
    };

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <IconButton onClick={() => navigate('/admin/riders')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight={700}>
                    Onboard New Rider
                </Typography>
            </Stack>

            <Paper sx={{ p: 4, maxWidth: 900, mx: 'auto', borderRadius: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* --- Section 1: Personal Details --- */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                                Personal Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                name="firstName"
                                required
                                value={formData.firstName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                required
                                value={formData.lastName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                type="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="phoneNumber"
                                required
                                value={formData.phoneNumber}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField select fullWidth label="Gender" name="gender" value={formData.gender} onChange={handleChange}>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </TextField>
                        </Grid>

                        {/* --- Section 2: Logistics --- */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                                Vehicle & Logistics
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Vehicle Type"
                                name="vehicle"
                                value={formData.vehicle}
                                onChange={handleChange}
                            >
                                <MenuItem value="bicycle">Bicycle</MenuItem>
                                <MenuItem value="motorcycle">Motorcycle / Bike</MenuItem>
                                <MenuItem value="car">Car</MenuItem>
                                <MenuItem value="van">Delivery Van</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Temporary Password"
                                name="password"
                                type="text"
                                value={formData.password}
                                onChange={handleChange}
                                helperText="Give this to the rider for their first login."
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 3 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={createMutation.isPending}
                                sx={{ py: 1.5, fontWeight: 700 }}
                            >
                                {createMutation.isPending ? <CircularProgress size={24} /> : 'Register Rider Profile'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
}
